import { useState, useEffect, useRef } from "react"
import { Search, ChevronDown, Check } from "lucide-react"

const API_URL = "https://api.centralapis.com/api/v1/country"

// Fonction helper pour faire des requêtes à l'API externe sans credentials
const fetchCountries = async (url) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Ne pas inclure credentials pour éviter les problèmes CORS
      credentials: 'omit',
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error fetching countries:", error)
    throw error
  }
}

export default function CountrySelector({ 
  value, 
  onChange, 
  placeholder = "Sélectionner un pays",
  className = "",
  disabled = false
}) {
  const [countries, setCountries] = useState([])
  const [filteredCountries, setFilteredCountries] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)

  // Charger les pays au montage
  useEffect(() => {
    loadCountries()
  }, [])

  // Filtrer les pays selon le terme de recherche
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCountries(countries)
    } else {
      const filtered = countries.filter(country => 
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.alpha2Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.callingCodes?.some(code => code.includes(searchTerm))
      )
      setFilteredCountries(filtered)
    }
  }, [searchTerm, countries])

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      // Focus sur le champ de recherche quand le dropdown s'ouvre
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const loadCountries = async () => {
    setLoading(true)
    try {
      const data = await fetchCountries(API_URL)
      
      if (data?.etat && data?.result) {
        // Trier les pays par nom
        const sorted = data.result.sort((a, b) => 
          a.name.localeCompare(b.name)
        )
        setCountries(sorted)
        setFilteredCountries(sorted)
      }
    } catch (error) {
      console.error("Error loading countries:", error)
      setCountries([])
      setFilteredCountries([])
    } finally {
      setLoading(false)
    }
  }

  const selectedCountry = countries.find(c => c.alpha2Code === value)

  const handleSelect = (country) => {
    onChange(country.alpha2Code)
    setIsOpen(false)
    setSearchTerm("")
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2.5 border-2 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] transition-all duration-300 ${
          disabled 
            ? "bg-gray-100 cursor-not-allowed border-gray-200" 
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedCountry ? (
            <>
              <img
                src={selectedCountry.flag}
                alt={selectedCountry.name}
                className="w-5 h-5 flex-shrink-0 rounded-sm object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
              <span className="text-sm font-medium text-gray-700 truncate">
                {selectedCountry.name}
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {/* Barre de recherche */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un pays..."
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a]"
              />
            </div>
          </div>

          {/* Liste des pays */}
          <div className="overflow-y-auto max-h-64">
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-500">
                Chargement...
              </div>
            ) : filteredCountries.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                Aucun pays trouvé
              </div>
            ) : (
              <div className="py-1">
                {filteredCountries.map((country) => {
                  const isSelected = value === country.alpha2Code
                  const callingCode = country.callingCodes?.[0] || ""
                  
                  return (
                    <button
                      key={country.alpha2Code}
                      type="button"
                      onClick={() => handleSelect(country)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                        isSelected ? "bg-blue-50" : ""
                      }`}
                    >
                      <img
                        src={country.flag}
                        alt={country.name}
                        className="w-6 h-4 flex-shrink-0 rounded-sm object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-700">
                          {country.name}
                        </div>
                        {callingCode && (
                          <div className="text-xs text-gray-500">
                            +{callingCode}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-[#203a8a] flex-shrink-0" />
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
